import React, {PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import './BottomSheet.scss';
import {Button, Icon} from '@mc/ui-v4';
import {mcClose} from '@mc/react-ui-icons';

type Props = {
    open: boolean;
    title?: ReactNode;
    extra?: ReactNode;
    onClose?: () => void;
    snapPoints?: number[];
    defaultSnapPointIndex?: number;
    onSnapChange?: (snapVh: number) => void;
    dismissibleByDrag?: boolean;
    hideHandle?: boolean;
    hideClose?: boolean;
    /**
     * Signál pro programové sbalení na nejmenší snap bod. Kdykoli se hodnota změní (např. inkrement
     * čítače), sheet se přichytí na nejnižší snap bod (index 0). Slouží k „sbalení" sheetu po akci
     * uživatele (např. klik na lokalitu v panelu hledání). Beze změny hodnoty se nic neděje.
     */
    collapseSignal?: number;
    /**
     * Signál pro programové vytažení sheetu na zadaný snap bod (`expandSnapPointIndex`). Kdykoli se
     * hodnota změní (inkrement čítače), sheet se přichytí na tento bod. Slouží k „vytažení" sheetu
     * po akci uživatele (např. focus vyhledávacího pole). Beze změny hodnoty se nic neděje.
     */
    expandSignal?: number;
    /** Cílový index snap bodu pro `expandSignal` (výchozí největší). */
    expandSnapPointIndex?: number;
    className?: string;
    handleBackgroundColor?: string;
};

const DEFAULT_SNAP_POINTS = [45, 92];
const CLOSE_MARGIN_PX = 48;

const winHeight = (): number => (typeof window !== 'undefined' ? window.innerHeight : 800);

const vhToPx = (vh: number, viewportH: number): number => Math.round((viewportH * vh) / 100);

export const BottomSheet: React.FC<PropsWithChildren<Props>> = ({
    open,
    title,
    extra,
    onClose,
    snapPoints,
    defaultSnapPointIndex,
    onSnapChange,
    dismissibleByDrag = false,
    hideHandle,
    hideClose,
    collapseSignal,
    expandSignal,
    expandSnapPointIndex,
    className,
    children,
    handleBackgroundColor = '#fff',
}) => {
    const [viewportH, setViewportH] = useState<number>(winHeight);
    const [dragHeight, setDragHeight] = useState<number | null>(null);
    const sheetRef = useRef<HTMLDivElement>(null);
    const dragStart = useRef<{y: number; height: number} | null>(null);
    // Živá výška tažení. Refem (ne stavem), protože pointermove je „continuous" událost –
    // React její setState nemusí stihnout commitnout dřív, než přijde pointerup, a closure
    // handlePointerUp by pak četla starou hodnotu dragHeight (panel by skočil zpět).
    const dragHeightRef = useRef<number | null>(null);
    // Zda během tažení došlo ke skutečnému pohybu. Slouží k potlačení syntetického `click`,
    // který prohlížeč u dotyku/pointeru vygeneruje po pointerup a jinak by přebil snap tažení.
    const draggedRef = useRef(false);

    const points = useMemo(() => {
        const source = snapPoints && snapPoints.length ? snapPoints : DEFAULT_SNAP_POINTS;
        return [...source].sort((a, b) => a - b).map(vh => ({vh, px: vhToPx(vh, viewportH)}));
    }, [snapPoints, viewportH]);

    const resizable = points.length > 1;
    const maxPx = points[points.length - 1].px;

    const nearestIndex = useCallback(
        (heightPx: number) => points.reduce((best, pt, i) => (Math.abs(pt.px - heightPx) < Math.abs(points[best].px - heightPx) ? i : best), 0),
        [points],
    );

    const [index, setIndex] = useState<number>(() => {
        const source = snapPoints?.length ? snapPoints : DEFAULT_SNAP_POINTS;

        if (defaultSnapPointIndex != null) {
            return Math.max(0, Math.min(defaultSnapPointIndex, source.length - 1));
        }

        return source.length - 1;
    });

    useEffect(() => {
        if (!open) {
            const source = snapPoints?.length ? snapPoints : DEFAULT_SNAP_POINTS;

            setDragHeight(null);

            if (defaultSnapPointIndex != null) {
                setIndex(Math.max(0, Math.min(defaultSnapPointIndex, source.length - 1)));
            } else {
                setIndex(source.length - 1);
            }
        }
    }, [open, snapPoints, defaultSnapPointIndex]);

    useEffect(() => {
        const onResize = () => setViewportH(winHeight());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        setIndex(i => Math.max(0, Math.min(i, points.length - 1)));
    }, [points.length]);

    const setSnapIndex = useCallback(
        (i: number) => {
            const clamped = Math.max(0, Math.min(i, points.length - 1));
            setIndex(clamped);
            onSnapChange?.(points[clamped]?.vh);
        },
        [points, onSnapChange],
    );

    // Programové sbalení na nejnižší snap bod při změně `collapseSignal` (první běh = mount ignorujeme,
    // ať nepřebijeme `defaultSnapPointIndex`).
    const prevCollapseSignalRef = useRef<number | undefined>(collapseSignal);
    useEffect(() => {
        if (prevCollapseSignalRef.current === collapseSignal) return;
        prevCollapseSignalRef.current = collapseSignal;
        setDragHeight(null);
        setSnapIndex(0);
    }, [collapseSignal, setSnapIndex]);

    // Programové vytažení na `expandSnapPointIndex` (výchozí největší) při změně `expandSignal`
    // (první běh = mount ignorujeme, ať nepřebijeme `defaultSnapPointIndex`).
    const prevExpandSignalRef = useRef<number | undefined>(expandSignal);
    useEffect(() => {
        if (prevExpandSignalRef.current === expandSignal) return;
        prevExpandSignalRef.current = expandSignal;
        setDragHeight(null);
        setSnapIndex(expandSnapPointIndex ?? points.length - 1);
    }, [expandSignal, expandSnapPointIndex, setSnapIndex, points.length]);

    const dragEnabled = resizable || dismissibleByDrag;

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!dragEnabled) return;
        const height = sheetRef.current?.getBoundingClientRect().height ?? points[index]?.px ?? maxPx;
        dragStart.current = {y: e.clientY, height};
        dragHeightRef.current = height;
        draggedRef.current = false;
        setDragHeight(height);
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragStart.current) return;
        // Tažení nahoru (menší clientY) zvětšuje výšku, dolů zmenšuje. Strop = největší snap bod.
        const next = Math.min(Math.max(dragStart.current.height + (dragStart.current.y - e.clientY), 0), maxPx);
        if (Math.abs(next - dragStart.current.height) > 4) draggedRef.current = true;
        dragHeightRef.current = next;
        setDragHeight(next);
    };

    const handlePointerUp = () => {
        if (!dragStart.current) return;
        const height = dragHeightRef.current ?? dragStart.current.height;
        dragStart.current = null;
        dragHeightRef.current = null;
        setDragHeight(null);

        if (dismissibleByDrag && height < points[0].px - CLOSE_MARGIN_PX) {
            onClose?.();
            return;
        }
        setSnapIndex(nearestIndex(height));
    };

    if (!open) return null;

    const isDragging = dragHeight != null;
    const heightPx = isDragging ? dragHeight! : points[Math.min(index, points.length - 1)]?.px;
    const style: React.CSSProperties = {height: `${heightPx}px`, ...(isDragging ? {transition: 'none'} : {})};

    return (
        <div ref={sheetRef} className={classNames('mapio-bottom-sheet drop-shadow', className)} style={style} role="dialog" aria-modal={false}>
            {!hideHandle && (
                <div
                    className={classNames('mapio-bottom-sheet-handle-area', {'is-static': !dragEnabled})}
                    style={{backgroundColor: handleBackgroundColor}}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onClick={() => {
                        // Po skutečném tažení potlačíme syntetický click, aby nepřebil snap z pointerup.
                        if (draggedRef.current) {
                            draggedRef.current = false;
                            return;
                        }
                        if (resizable) setSnapIndex((index + 1) % points.length);
                    }}
                >
                    <span className="mapio-bottom-sheet-handle" />
                </div>
            )}
            {(title || extra || (!hideClose && onClose)) && (
                <div className="mapio-bottom-sheet-header">
                    <div className="mapio-bottom-sheet-title">{title}</div>
                    <div className="mapio-bottom-sheet-actions">
                        {extra}
                        {!hideClose && onClose && (
                            <Button type="button" /*style="link"*/ icon={<Icon icon={mcClose} size={'lg'} />} onClick={onClose} aria-label="Zavřít" />
                        )}
                    </div>
                </div>
            )}
            <div className="mapio-bottom-sheet-body">{children}</div>
        </div>
    );
};
